import { useState, useEffect } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Check, Square } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { acceptTrialInvitation } from "@/lib/apiClient";
import { fetchReferralInviteBanner } from "@/lib/referralInviteBanner";
import { fetchSignupPlanPricing, formatPesos } from "@/lib/signupPlanPricing";
import { PASSWORD_POLICY_DESCRIPTION, passwordPolicyError } from "@/lib/passwordPolicy";

// =====================================================================
// Self-serve signup
//   /signup        - account creation with plan context
//   /signup/clinic - existing post-auth clinic setup fallback
// =====================================================================
//
// Plan prices on this page come from the database (public.signup_plan_pricing
// RPC), so whatever Superadmin has configured in admin > Pricing flows
// through automatically. Never hardcode a peso amount here.

const SIGNUP_PLAN_STORAGE_KEY = "dental_os_signup_plan";
const SIGNUP_REFERRAL_STORAGE_KEY = "dental_os_signup_referral_code";
export const PENDING_OWNER_SIGNUP_KEY = "dental_os_pending_owner_signup";
const PENDING_INVITE_KEY = "dental_os_pending_invite_code";
const INVITE_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{3}-[A-HJ-NP-Z2-9]{3}$/;

const PLAN_STATIC = {
  solo: {
    code: "solo",
    label: "Solo",
    title: "Solo Practice",
    planTag: "14-day trial",
    headline: "14 days free. Built for solo clinics.",
    description: "One doctor, front desk, checkout, and recall.",
    capacity: [
      { icon: "doctor", label: "1 doctor" },
      { icon: "team", label: "2 staff" },
      { icon: "branch", label: "1 branch" },
    ],
    featureLabel: "Included",
    included: [
      "Dynamic charting",
      "Checkout, billing & payments",
      "Recall flows",
    ],
    unavailable: [
      "Tx Plan & Ortho Center",
      "Detailed reports",
    ],
    bullets: [
      "Built for owner-dentists and small teams",
      "No credit card until trial ends",
      "Cancel in two clicks, no questions",
      "Upgrade to Clinic when your team grows",
    ],
  },
  clinic: {
    code: "clinic",
    label: "Clinic",
    title: "Clinic",
    planTag: "Founding rate",
    headline: "14 days free. No card needed.",
    description: "Multi-doctor. Full clinical and revenue stack.",
    capacity: [
      { icon: "team", label: "Unlimited doctors", featured: true },
      { icon: "branch", label: "2 branches" },
    ],
    featureLabel: "Everything in Solo, plus",
    included: [
      "Tx Plan Center",
      "Ortho Center",
      "Detailed reports & analytics",
      "Unlimited doctors & staff",
      "Multi-branch support",
    ],
    unavailable: [],
    bullets: [
      "Full access to all features during trial",
      "No credit card until trial ends",
      "Cancel in two clicks, no questions",
      "Your founding rate is locked at signup",
    ],
  },
};

// Build the per-plan display object from live pricing. `pricing` is the
// parsed payload from fetchSignupPlanPricing(), or null while loading /
// if the RPC failed. When pricing is null we still render the page but
// the price slots show "—" rather than a misleading hardcoded number.
function buildPlanContent(planCode, pricing) {
  const base = PLAN_STATIC[planCode] || PLAN_STATIC.solo;
  const live = pricing ? pricing[planCode] : null;
  const priceStr = live && live.monthly_price_centavos !== null
    ? formatPesos(live.monthly_price_centavos) + "/mo"
    : "—";
  const isFounding = Boolean(live && live.is_founding_rate);
  const slotsLeft = live && typeof live.founding_slots_remaining === "number"
    ? live.founding_slots_remaining
    : null;

  const planLabel = base.label;
  const badge = isFounding && slotsLeft !== null
    ? `Founding ${planLabel} · ${slotsLeft} spots left`
    : `${planLabel} plan`;
  const body = isFounding
    ? `You're signing up for the ${planLabel} plan at the founding rate of ${priceStr}, locked for life once the cohort closes.`
    : `You're signing up for the ${planLabel} plan at ${priceStr} after your trial ends.`;

  return {
    ...base,
    price: priceStr,
    planDisplay: `${base.label}, ${priceStr}`,
    badge,
    body,
    pricingLoaded: pricing !== null,
  };
}

function normalizePlan(value) {
  return value === "clinic" ? "clinic" : value === "solo" ? "solo" : null;
}

function readSignupPlan(search, routeReferralCode) {
  const referralInviteCode = readCurrentReferralCode(search, routeReferralCode);
  if (referralInviteCode) {
    window.sessionStorage.setItem(SIGNUP_PLAN_STORAGE_KEY, "clinic");
    return "clinic";
  }
  const urlPlan = normalizePlan(new URLSearchParams(search || "").get("plan"));
  if (urlPlan) {
    window.sessionStorage.setItem(SIGNUP_PLAN_STORAGE_KEY, urlPlan);
    return urlPlan;
  }
  const storedPlan = normalizePlan(window.sessionStorage.getItem(SIGNUP_PLAN_STORAGE_KEY));
  const fallback = storedPlan || "clinic";
  window.sessionStorage.setItem(SIGNUP_PLAN_STORAGE_KEY, fallback);
  return fallback;
}

function readReferralCode(search, routeCode) {
  const params = new URLSearchParams(search || "");
  const code = (routeCode || params.get("ref") || "").trim().toLowerCase();
  if (code) {
    window.sessionStorage.setItem(SIGNUP_REFERRAL_STORAGE_KEY, code);
    return code;
  }
  return (window.sessionStorage.getItem(SIGNUP_REFERRAL_STORAGE_KEY) || "").trim().toLowerCase();
}

function readCurrentReferralCode(search, routeCode) {
  const params = new URLSearchParams(search || "");
  return (routeCode || params.get("ref") || "").trim().toLowerCase();
}

function ownerNameFromSession(session) {
  const metadata = session?.user?.user_metadata || {};
  const metadataName = metadata.full_name || metadata.display_name || metadata.name;
  const emailLocalPart = session?.user?.email?.split("@")?.[0]?.replace(/[._-]+/g, " ");
  const candidate = (metadataName || emailLocalPart || "Clinic Owner").trim();
  if (!candidate || candidate.includes("@") || ["owner", "doctor"].includes(candidate.toLowerCase())) {
    return "Clinic Owner";
  }
  return candidate;
}

function cleanDisplayText(value) {
  const text = String(value || "").trim();
  return text && text.toLowerCase() !== "null" && text.toLowerCase() !== "undefined" ? text : "";
}

function referralInitials(name) {
  const withoutTitle = cleanDisplayText(name).replace(/^(dr\.?|dra\.?|doctor)\s+/i, "");
  const letters = withoutTitle
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return (letters || "DO").slice(0, 2);
}

function readPendingOwnerSignup() {
  try {
    return JSON.parse(window.localStorage.getItem(PENDING_OWNER_SIGNUP_KEY) || "null");
  } catch {
    return null;
  }
}

function writePendingOwnerSignup(payload) {
  window.localStorage.setItem(PENDING_OWNER_SIGNUP_KEY, JSON.stringify({
    ...payload,
    createdAt: new Date().toISOString(),
  }));
}

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { referralCode: routeReferralCode } = useParams();
  const { session, signUp, signOut, createClinicForOwner, refreshClinics, switchClinic, clinics = [] } = useAuth();
  const [step, setStep] = useState(session ? 2 : 1);
  const [signupPlan, setSignupPlan] = useState(() => readSignupPlan(location.search, routeReferralCode));
  const [referralCode, setReferralCode] = useState(() => readReferralCode(location.search, routeReferralCode));
  const [referralInviteCode, setReferralInviteCode] = useState(() => readCurrentReferralCode(location.search, routeReferralCode));
  const [referralInvite, setReferralInvite] = useState(null);
  const [signupPricing, setSignupPricing] = useState(null);
  const [accountCreated, setAccountCreated] = useState(false);
  const [signupSession, setSignupSession] = useState(null);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [clinicAddress, setClinicAddress] = useState("");
  const [clinicPhone, setClinicPhone] = useState("");
  const [chairCount, setChairCount] = useState(1);
  const [existingClinicCode, setExistingClinicCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);

  const pendingInviteCode = window.sessionStorage.getItem(PENDING_INVITE_KEY);
  const inviteToken = new URLSearchParams(location.search).get("invite_token");
  const existingClinics = Array.isArray(clinics) ? clinics : [];
  const hasExistingClinics = !!session && existingClinics.length > 0;
  const activeSignupSession = session || signupSession;

  useEffect(() => {
    setSignupPlan(readSignupPlan(location.search, routeReferralCode));
    setReferralCode(readReferralCode(location.search, routeReferralCode));
    setReferralInviteCode(readCurrentReferralCode(location.search, routeReferralCode));
  }, [location.search, routeReferralCode]);

  useEffect(() => {
    let cancelled = false;
    if (!referralInviteCode) {
      setReferralInvite(null);
      return () => { cancelled = true; };
    }
    fetchReferralInviteBanner(referralInviteCode).then((invite) => {
      if (!cancelled) setReferralInvite(invite);
    });
    return () => { cancelled = true; };
  }, [referralInviteCode]);

  useEffect(() => {
    let cancelled = false;
    fetchSignupPlanPricing().then((p) => {
      if (!cancelled) setSignupPricing(p);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (session && step === 1) setStep(2);
  }, [session, step]);

  useEffect(() => {
    if (session && !ownerName.trim()) {
      const pending = readPendingOwnerSignup();
      setOwnerName(pending?.ownerName || ownerNameFromSession(session));
    }
  }, [session, ownerName]);

  useEffect(() => {
    if (!session) return;
    const pending = readPendingOwnerSignup();
    if (!pending) return;
    setFirstName((current) => current || pending.firstName || "");
    setLastName((current) => current || pending.lastName || "");
    setEmail((current) => current || pending.email || session.user?.email || "");
    setOwnerName((current) => current || pending.ownerName || ownerNameFromSession(session));
    setClinicName((current) => current || pending.clinicName || "");
    setSignupPlan((current) => pending.plan || current);
    setReferralCode((current) => pending.referralCode || current);
    setReferralInviteCode((current) => pending.referralInviteCode || current);
    setStep(2);
  }, [session]);

  if (session && pendingInviteCode) {
    return <Navigate to={`/join?code=${encodeURIComponent(pendingInviteCode)}`} replace />;
  }

  if (!session && verificationEmail) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle="Verify your email before clinic setup."
        footer={
          <>Already verified? <Link data-testid="signup-verify-login" to="/login" className="text-teal-700 font-medium hover:underline">Sign in</Link></>
        }
      >
        <div data-testid="signup-verify-email" className="space-y-3">
          <p className="text-sm leading-6 text-slate-600">
            We sent a verification link to <span className="font-semibold text-slate-900">{verificationEmail}</span>.
            Click that link to confirm your email, then Dental OS will bring you back here to finish clinic setup.
          </p>
          <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
            The link expires after one hour. If it expires or was already used, sign in or start signup again to request a fresh email.
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (hasExistingClinics) {
    return <Navigate to="/dashboard" replace />;
  }

  // Referral invites are locked to Clinic; otherwise the visitor can switch
  // plans on the page. Persist to sessionStorage so the choice survives the
  // step-2 hand-off and any same-page re-render.
  function selectPlan(planCode) {
    if (referralInviteCode) return;
    const normalized = normalizePlan(planCode) || "solo";
    setSignupPlan(normalized);
    window.sessionStorage.setItem(SIGNUP_PLAN_STORAGE_KEY, normalized);
  }

  function handleExistingClinicCodeSubmit() {
    setError(null);
    const code = existingClinicCode.trim().toUpperCase();
    if (!INVITE_CODE_PATTERN.test(code)) {
      setError("Enter a valid clinic invite code in the format XXX-XXX-XXX.");
      return;
    }
    window.sessionStorage.setItem(PENDING_INVITE_KEY, code);
    navigate(`/join?code=${encodeURIComponent(code)}`);
  }

  async function handleAccountSubmit(e) {
    e.preventDefault();
    setError(null);
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedClinicName = clinicName.trim();
    if (!trimmedFirstName) { setError("First name is required."); return; }
    if (!trimmedLastName) { setError("Last name is required."); return; }
    if (!trimmedClinicName) { setError("Clinic name is required."); return; }
    const policyError = passwordPolicyError(password);
    if (policyError) { setError(policyError); return; }
    const normalizedEmail = email.trim();
    const pendingPayload = {
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      ownerName: `${trimmedFirstName} ${trimmedLastName}`.trim(),
      clinicName: trimmedClinicName,
      email: normalizedEmail,
      plan: signupPlan,
      referralCode: referralCode || null,
      referralInviteCode: referralInviteCode || null,
      inviteToken: inviteToken || null,
    };
    setSubmitting(true);
    writePendingOwnerSignup(pendingPayload);
    const { data: signupData, error: signupError } = await signUp(normalizedEmail, password, {
      emailRedirectTo: `${window.location.origin}/auth/confirmed?intent=owner`,
      data: {
        full_name: pendingPayload.ownerName,
        clinic_name: trimmedClinicName,
        signup_plan: signupPlan,
      },
    });
    if (signupError) {
      setSubmitting(false);
      setError(signupError.message ?? "Sign-up failed.");
      return;
    }
    if (!signupData?.session) {
      setSubmitting(false);
      setVerificationEmail(normalizedEmail);
      return;
    }
    setSubmitting(false);
    setSignupSession(signupData.session);
    setOwnerName(`${trimmedFirstName} ${trimmedLastName}`.trim());
    setClinicName(trimmedClinicName);
    setAccountCreated(true);
    setStep(2);
  }

  async function handleStep2(e) {
    e.preventDefault();
    setError(null);
    if (!activeSignupSession) {
      setError("Please sign in to finish clinic setup.");
      return;
    }
    const resolvedOwnerName = ownerName.trim() || ownerNameFromSession(activeSignupSession);
    if (!resolvedOwnerName) { setError("Your name is required."); return; }
    if (!clinicName.trim()) { setError("Clinic name is required."); return; }
    if (!clinicAddress.trim()) { setError("Clinic address is required."); return; }
    if (!clinicPhone.trim()) { setError("Clinic phone is required."); return; }
    const normalizedChairCount = Number(chairCount);
    if (!Number.isInteger(normalizedChairCount) || normalizedChairCount < 1 || normalizedChairCount > 20) {
      setError("Number of chairs must be between 1 and 20.");
      return;
    }
    setSubmitting(true);
    const { data, error: err } = await createClinicForOwner({
      owner_display_name:          resolvedOwnerName,
      name:                       clinicName.trim(),
      country:                    "PH",
      currency:                   "PHP",
      timezone:                   "Asia/Manila",
      p_seed_sample_patient:      false,
      p_address:                  clinicAddress.trim(),
      p_phone:                    clinicPhone.trim(),
      p_chair_count:              normalizedChairCount,
      p_email:                    email.trim() || activeSignupSession?.user?.email || "",
      plan:                       signupPlan,
      is_founding:                true,
      referral_code:              referralCode || null,
    });
    setSubmitting(false);
    if (err) { setError(err.message ?? "Could not create clinic."); return; }
    const newClinicId = data?.clinic_id;
    if (newClinicId && switchClinic) {
      const { error: switchError } = await switchClinic(newClinicId);
      if (switchError) {
        setError(switchError.message ?? "Clinic was created, but we could not switch to it.");
        return;
      }
    } else {
      await refreshClinics();
    }
    if (inviteToken) {
      try {
        const accepted = await acceptTrialInvitation(inviteToken);
        if (accepted?.ok === false) {
          setError(accepted.message || "Clinic created, but the invitation could not be marked accepted.");
          return;
        }
      } catch (inviteErr) {
        setError(inviteErr.message || "Clinic created, but the invitation could not be marked accepted.");
        return;
      }
    }
    window.localStorage.removeItem(PENDING_OWNER_SIGNUP_KEY);
    window.sessionStorage.removeItem(SIGNUP_REFERRAL_STORAGE_KEY);
    navigate("/dashboard", { replace: true });
  }

  async function handleSignOutToLogin() {
    await signOut();
    navigate("/login", { replace: true });
  }

  const titles = { 1: "Create your account", 2: "Tell us about your clinic" };
  const subs   = { 1: "Step 1 of 2 · Email and password",
                   2: "Step 2 of 2 · Owner and clinic details" };

  if (!session && !accountCreated) {
    const plan = buildPlanContent(signupPlan, signupPricing);
    const plans = {
      solo: buildPlanContent("solo", signupPricing),
      clinic: buildPlanContent("clinic", signupPricing),
    };
    return (
      <div data-testid="signup-account-page" className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-10">
        <div className="flex w-full max-w-5xl min-w-0 flex-col gap-3">
          <ReferralInviteBanner invite={referralInvite} />
          <div className="w-full min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <div className="inline-flex items-center gap-2">
              <div className="h-5 w-5 rounded bg-teal-600" aria-hidden="true" />
              <span className="text-sm font-semibold text-slate-900">Dental OS</span>
            </div>
            <Link to="/pricing" className="text-xs font-medium text-slate-600 hover:text-teal-700">
              Back to pricing
            </Link>
          </div>

          <div className="grid min-w-0 lg:grid-cols-[1fr_1.05fr]">
            <MarketingPanel
              plan={plan}
              plans={plans}
              planCode={signupPlan}
              onSelectPlan={selectPlan}
              pickerLocked={Boolean(referralInviteCode)}
            />

            <section className="min-w-0 border-t border-slate-200 p-6 sm:p-8 lg:border-l lg:border-t-0 lg:p-12">
              <div className="mx-auto max-w-sm">
                <h1 data-testid="auth-title" className="text-xl font-bold text-slate-900">Create your account</h1>
                <ExistingClinicCodePanel
                  code={existingClinicCode}
                  onCodeChange={setExistingClinicCode}
                  onContinue={handleExistingClinicCodeSubmit}
                />
                <form onSubmit={handleAccountSubmit} className="mt-6 space-y-5" data-testid="signup-account-form">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field id="signup-first-name" testid="signup-first-name-input" label="First name"
                           value={firstName} onChange={setFirstName} placeholder="Maria" required />
                    <Field id="signup-last-name" testid="signup-last-name-input" label="Last name"
                           value={lastName} onChange={setLastName} placeholder="Santos" required />
                  </div>

                  <Field id="signup-clinic-name" testid="signup-clinic-name-input" label="Clinic name"
                         value={clinicName} onChange={setClinicName} placeholder="Santos Dental Clinic" required />

                  <Field id="signup-email" testid="signup-email-input" label="Work email" type="email" autoComplete="email"
                         value={email} onChange={setEmail} placeholder="maria@santosdental.ph" required />

                  <Field id="signup-password" testid="signup-password-input" label="Password" type="password"
                         autoComplete="new-password" value={password} onChange={setPassword}
                         placeholder="10+ chars, Aa, 1, symbol" required />
                  <p className="-mt-3 text-xs text-slate-500">{PASSWORD_POLICY_DESCRIPTION}</p>

                  <PlanField plan={plan} />

                  {error ? <p data-testid="signup-error" className="text-sm text-rose-600">{error}</p> : null}

                  <button
                    type="submit"
                    data-testid="signup-submit-button"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 py-2.5 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:bg-teal-400"
                  >
                    {submitting ? "Working..." : "Start free trial"}
                    {!submitting ? <ArrowRight className="h-4 w-4" aria-hidden="true" /> : null}
                  </button>

                  <p className="text-center text-[11px] leading-5 text-slate-500">
                    By signing up you agree to our Terms of Service and Privacy Policy.
                  </p>
                </form>

                <div className="mt-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-center text-[11px] leading-4 text-slate-500">already have an account?</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
                <div className="mt-2 text-center">
                  <Link data-testid="signup-go-login" to="/login" className="text-sm font-medium text-teal-700 hover:underline">Sign in</Link>
                </div>
              </div>
            </section>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthLayout
      title={titles[step]}
      subtitle={subs[step]}
      footer={step === 1 ? (
        <>Already have an account? <Link data-testid="signup-go-login" to="/login" className="text-teal-700 font-medium hover:underline">Sign in</Link></>
      ) : session ? (
        <>Wrong account? <button type="button" data-testid="signup-signout-login" onClick={handleSignOutToLogin} className="text-teal-700 font-medium hover:underline">Sign out and log in</button></>
      ) : null}
    >
      <StepRail step={step} />

      {step === 1 && !session ? (
        <form onSubmit={handleAccountSubmit} className="space-y-5" data-testid="signup-step1-form">
          <Field id="signup-email" testid="signup-email-input" label="Email" type="email" autoComplete="email"
                 value={email} onChange={setEmail} placeholder="you@clinic.example" required />
          <Field id="signup-password" testid="signup-password-input" label="Password" type="password"
                 autoComplete="new-password" value={password} onChange={setPassword}
                 placeholder="10+ chars, Aa, 1, symbol" required />
          <p className="-mt-3 text-xs text-slate-500">{PASSWORD_POLICY_DESCRIPTION}</p>
          {error ? <p data-testid="signup-error" className="text-sm text-rose-600">{error}</p> : null}
          <Submit testid="signup-step1-button" busy={submitting} label="Continue" />
        </form>
      ) : null}

      {step === 2 && activeSignupSession ? (
        <form onSubmit={handleStep2} className="space-y-5" data-testid="signup-step2-form">
          <Field id="signup-clinic-name" testid="signup-clinic-name-input" label="Clinic name"
                 value={clinicName} onChange={setClinicName} placeholder="Sunrise Dental" required />

          <Field id="signup-clinic-address" testid="signup-clinic-address-input" label="Clinic address"
                 value={clinicAddress} onChange={setClinicAddress} placeholder="Street, city, province" required />

          <Field id="signup-clinic-phone" testid="signup-clinic-phone-input" label="Clinic phone"
                 value={clinicPhone} onChange={setClinicPhone} placeholder="09171234567" required />

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="signup-chair-count">
              Number of chairs
            </label>
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                data-testid="signup-chair-count-decrement"
                onClick={() => setChairCount((v) => Math.max(1, Number(v) - 1))}
                className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                aria-label="Decrease chair count"
              >
                -
              </button>
              <input
                id="signup-chair-count"
                data-testid="signup-chair-count-input"
                type="number"
                min="1"
                max="20"
                step="1"
                required
                value={chairCount}
                onChange={(e) => setChairCount(e.target.value === "" ? "" : Number(e.target.value))}
                className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-center text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
              />
              <button
                type="button"
                data-testid="signup-chair-count-increment"
                onClick={() => setChairCount((v) => Math.min(20, Number(v) + 1))}
                className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                aria-label="Increase chair count"
              >
                +
              </button>
            </div>
          </div>

          {error ? <p data-testid="signup-error" className="text-sm text-rose-600">{error}</p> : null}
          <Submit testid="signup-step2-button" busy={submitting} label="Start 14-day trial" />
        </form>
      ) : null}

      {step === 2 && !activeSignupSession ? <Navigate to="/signup" replace /> : null}
    </AuthLayout>
  );
}

function ReferralInviteBanner({ invite }) {
  const clinicName = cleanDisplayText(invite?.referring_clinic_name || invite?.clinic_name);
  const doctorName = cleanDisplayText(invite?.referrer_display_name || invite?.doctor_display_name);
  const inviterName = doctorName || clinicName;
  if (!clinicName || !inviterName) return null;

  return (
    <div
      data-testid="referral-invite-banner"
      className="sticky top-0 z-20 flex w-full items-center gap-3 rounded-md border border-emerald-500 bg-emerald-50/95 px-4 py-3 shadow-[0_1px_0_rgba(6,95,70,0.05)]"
    >
      <div
        data-testid="referral-invite-initials"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-800 text-xs font-bold text-white"
        aria-hidden="true"
      >
        {referralInitials(inviterName)}
      </div>
      <div className="min-w-0 leading-tight">
        <p className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-emerald-950">
          <span className="truncate">{inviterName} invited you to Dental OS</span>
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-700" aria-hidden="true" />
        </p>
        <p className="mt-0.5 truncate text-xs text-emerald-700">
          {clinicName} · verified clinic on Dental OS
        </p>
      </div>
    </div>
  );
}

function ExistingClinicCodePanel({ code, onCodeChange, onContinue }) {
  return (
    <div data-testid="signup-existing-clinic-panel" className="mt-5 rounded-lg border border-teal-100 bg-teal-50 p-3">
      <div className="text-sm font-semibold text-slate-900">Joining an existing clinic?</div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          id="signup-existing-clinic-code"
          data-testid="signup-existing-clinic-code-input"
          value={code}
          onChange={(e) => onCodeChange(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onContinue();
            }
          }}
          placeholder="ABC-DEF-GHJ"
          maxLength={11}
          className="min-w-0 flex-1 rounded-lg border border-teal-200 bg-white px-3 py-2 text-sm font-mono uppercase tracking-wider text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
        />
        <button
          type="button"
          data-testid="signup-existing-clinic-code-submit"
          onClick={onContinue}
          className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-teal-700 ring-1 ring-inset ring-teal-200 transition hover:bg-teal-100"
        >
          Join clinic
        </button>
      </div>
    </div>
  );
}

function PlanPicker({ value, onSelect }) {
  const options = [
    { code: "solo", label: "Solo" },
    { code: "clinic", label: "Clinic" },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Choose your plan"
      data-testid="signup-plan-picker"
      className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-1"
    >
      {options.map((opt) => {
        const active = value === opt.code;
        return (
          <button
            key={opt.code}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`signup-plan-option-${opt.code}`}
            onClick={() => onSelect(opt.code)}
            className={`rounded-md px-5 py-1.5 text-sm font-semibold transition ${
              active
                ? "bg-white text-teal-700 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function MarketingPanel({ plan, plans, planCode, onSelectPlan, pickerLocked }) {
  return (
    <section className="flex min-h-[34rem] min-w-0 items-center p-5 sm:p-7 lg:p-10" data-testid="signup-marketing-panel">
      <div className="w-full min-w-0 max-w-[34rem]">
        {!pickerLocked ? <PlanPicker value={planCode} onSelect={onSelectPlan} /> : null}

        <div className={`inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700 ${pickerLocked ? "" : "mt-6"}`}>
          <Square className="h-3 w-3 fill-teal-100 text-teal-500" aria-hidden="true" />
          {plan.badge}
        </div>

        <h2 className="mt-5 max-w-full break-words text-2xl font-bold leading-tight text-slate-950">
          Pick the workflow tier, not just the lowest price.
        </h2>
        <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
          Solo covers the one-doctor front desk. Clinic is for Tx Plans, ortho,
          reports, and multi-doctor work.
        </p>

        <PlanInclusions plans={plans} />

        <p className="mt-5 text-xs leading-5 text-slate-500">
          14 days free · no card · cancel in two clicks
          {pickerLocked ? " · referral signups stay on Clinic" : ""}
        </p>
      </div>
    </section>
  );
}

function PlanInclusions({ plans }) {
  return (
    <div
      data-testid="signup-plan-inclusions"
      className="mt-7 rounded-lg border border-slate-200 bg-white/80 p-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <InclusionColumn
          heading="Solo includes"
          description={`${plans.solo.price} · one-doctor front desk basics`}
          items={plans.solo.included}
        />
        <div className="border-t border-slate-200 pt-4 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
          <InclusionColumn
            heading="Clinic adds"
            description={`${plans.clinic.price} · treatment workflow stack`}
            items={plans.clinic.included}
            accent
          />
        </div>
      </div>
      <div className="mt-4 rounded-md bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-500">
        <span className="font-semibold text-slate-700">Solo does not include: </span>
        {plans.solo.unavailable.join(", ")}.
      </div>
    </div>
  );
}

function InclusionColumn({ heading, description, items, accent = false }) {
  return (
    <div>
      <p className={`text-xs font-bold uppercase tracking-[0.18em] ${accent ? "text-teal-700" : "text-slate-500"}`}>
        {heading}
      </p>
      <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
      <ul className="mt-3 space-y-2.5 text-sm text-slate-700">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${accent ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>
              <Check className="h-3 w-3" aria-hidden="true" />
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlanField({ plan }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="signup-plan">Plan</label>
      <div className="relative mt-1">
        <input
          id="signup-plan"
          data-testid="signup-plan-display"
          type="text"
          readOnly
          value={plan.planDisplay}
          className="w-full rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 pr-32 text-sm font-semibold text-teal-900 outline-none"
        />
        <span className="pointer-events-none absolute inset-y-1 right-1 inline-flex items-center rounded-md bg-white/80 px-3 text-[11px] font-semibold leading-none text-teal-700">
          {plan.planTag}
        </span>
      </div>
    </div>
  );
}

function StepRail({ step }) {
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="signup-step-rail">
      {[1,2].map((i) => (
        <div key={i} data-testid={`signup-step-pip-${i}`}
             className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-teal-600" : "bg-slate-200"}`} />
      ))}
    </div>
  );
}

function Field({ id, testid, label, type="text", autoComplete, value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor={id}>{label}</label>
      <input id={id} data-testid={testid} type={type} autoComplete={autoComplete} required={required}
             value={value} onChange={(e) => onChange(e.target.value)}
             className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
             placeholder={placeholder} />
    </div>
  );
}

function Submit({ testid, busy, label }) {
  return (
    <button type="submit" data-testid={testid} disabled={busy}
            className="w-full rounded-lg bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white text-sm font-semibold py-2.5 transition">
      {busy ? "Working…" : label}
    </button>
  );
}
